import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    CreateUIAnalyticsEvent,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";

import {
    useBxpWebStargateClient,
    ProductActivationStatusResponse
} from "@/libraries/bxp-web-stargate-client/src/index";
import {
    useBXPExpressClient,
    useCofsClient
} from "@/libraries/signup/bxpkit-core/src";

import { ConfirmationPageType } from "./use-confirmation-page-type";

import {
    useConfirmationPageType,
    useFreeBundlesStatus,
    useJiraTemplateChoice,
    useSearchParameters,
    useUserSegmentationStatus
} from "./";

export const DEFAULT_TIMEOUT = 10 * 60000;
export const DEFAULT_RETRY_INTERVAL = 5000;
export const DEFAULT_CREATE_CLOUD_STATUS: CreateCloudStatus = "pending";

export type CreateCloudStatus = "pending" | "success" | "timeout" | "error";

export enum ConfirmationPageOverlayVariants {
    BUNDLES_MODAL = "bundles_modal",
    VIDEO_OVERLAY = "video_overlay",
    CONTROL = "control",
    UNENROLLED = "unenrolled"
}

export type FeatureFlags = {
    readonly provisionSecondPartyApps?: boolean;
    readonly enableBundlesAfterSignup?: boolean;
    readonly enableConfirmationPageOverlay?: ConfirmationPageOverlayVariants;
    readonly enableCutSignUpStepsInHalf?: boolean;
};

export type CloudStatus = {
    readonly isReferredFromVerifyEmail: boolean;
    readonly createCloudStatus: CreateCloudStatus;
    readonly progress: number;
    readonly featureFlags: FeatureFlags;
    readonly deferRedirect: boolean;
    readonly setDeferRedirect?: (deferRedirect: boolean) => void;
    readonly redirectUrl: string;
};

export function useCloudStatus() {
    return useContext(CloudStatusContext);
}

const CloudStatusContext = createContext<CloudStatus>({
    isReferredFromVerifyEmail: false,
    createCloudStatus: DEFAULT_CREATE_CLOUD_STATUS,
    progress: 0,
    featureFlags: {
        provisionSecondPartyApps: false,
        enableBundlesAfterSignup: false
    },
    deferRedirect: false,
    redirectUrl: ""
});

type ProgressBarStatusProps = {
    readonly startTime: number;
    readonly pivotPercentage: number;
    readonly progressBarPivotPoint: number;
    readonly slowProgressIncrement: number;
    readonly slowProgress: number;
    readonly interval?: NodeJS.Timer;
};

function getProgressbarStatus({
    startTime,
    pivotPercentage,
    progressBarPivotPoint,
    slowProgressIncrement,
    slowProgress,
    interval
}: ProgressBarStatusProps) {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= DEFAULT_TIMEOUT) {
        // @ts-ignore
        clearInterval(interval);
        return 1;
    } else if (elapsedTime <= progressBarPivotPoint) {
        const rapidProgress =
            (pivotPercentage / progressBarPivotPoint) * elapsedTime;
        return rapidProgress;
    } else {
        slowProgress =
            slowProgress == 0
                ? pivotPercentage
                : slowProgress + slowProgressIncrement;
        return slowProgress;
    }
}

export type CloudStatusProviderProps = {
    readonly retryInterval?: number;
    readonly timeout?: number;
    readonly isProduction?: boolean;
    readonly featureFlags: FeatureFlags;
    readonly provisioningRequestId: string;
    readonly onDemandUrl: string;
    readonly cloudId: string;
    readonly platform?: string;
    readonly requestId: string;
};

type GetCloudStatusType = () => Promise<boolean>;

export const CloudStatusProvider = ({
    retryInterval = DEFAULT_RETRY_INTERVAL,
    timeout = DEFAULT_TIMEOUT,
    isProduction = false,
    children,
    featureFlags,
    provisioningRequestId,
    onDemandUrl,
    cloudId: cloudIdProp,
    platform,
    requestId
}: PropsWithChildren<CloudStatusProviderProps>) => {
    const cofsClient = useCofsClient();
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        referrer,
        products,
        siteRenameRequestId,
        dataRegion,
        admin,
        isFoundingAdmin,
        signupSource,
        provisionApp,
        appKey
    } = useSearchParameters();
    const isProductJsw =
        products.length === 1 && products.includes("jira-software.ondemand");
    const isProductConfluence =
        products.length === 1 && products.includes("confluence.ondemand");
    const isFreeBundlesEnabled =
        featureFlags?.enableBundlesAfterSignup && isProductJsw;
    const is15secondProvisioningEnabled = isProductJsw || isProductConfluence;
    const isPageOverlayModalEnabled =
        useConfirmationPageOverlayModal(featureFlags);
    const { bundleProvisioningStatus, setBundleProvisioningStatus } =
        useFreeBundlesStatus();
    const [createCloudStatus, setCreateCloudStatus] =
        useState<CreateCloudStatus>(timeout > 0 ? "pending" : "timeout");
    const [cloudId, setCloudId] = useState<string>(cloudIdProp);
    const hasRequiredParameters = !!onDemandUrl && !!products.length;

    const [progress, setProgress] = useState(0);
    const isReferredFromVerifyEmail = referrer === "verify_email";
    const { shouldShowUserSegmentationPage } =
        useUserSegmentationStatus(products);

    const [deferRedirect, setDeferRedirect] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState("");

    const currentScreen = useConfirmationPageType(
        createCloudStatus,
        deferRedirect
    );
    const finalProvisioningScreens: ConfirmationPageType[] = useMemo(
        () => ["robots", "jira-animated-provisioning-screen", "redirect"],
        []
    );

    const { templateChoice } = useJiraTemplateChoice();

    const createRedirectUrl = useCallback(() => {
        const url: URL = new URL(
            createBaseUrl(
                isProduction,
                onDemandUrl,
                products,
                dataRegion,
                cloudId,
                admin,
                createAnalyticsEvent
            )
        );

        // This causes browser to retry for 30 seconds if site isn't ready
        url.searchParams.set("src", "signup");

        if (templateChoice) {
            url.pathname = url.pathname + "welcome/software";
            url.searchParams.set("jsw_acp", "true");
            url.searchParams.set("template_choice", templateChoice);
        }

        if (siteRenameRequestId) {
            url.searchParams.set("rename", "true");
        }
        if (signupSource === "bbbundle" && isFoundingAdmin === "true") {
            url.searchParams.set("signupSource", signupSource);
            url.searchParams.set("isFoundingAdmin", isFoundingAdmin);
        }

        return url.toString();
    }, [
        admin,
        cloudId,
        products,
        dataRegion,
        onDemandUrl,
        signupSource,
        isProduction,
        templateChoice,
        isFoundingAdmin,
        siteRenameRequestId,
        createAnalyticsEvent
    ]);

    const [getCloudStatus, setCloudStatus] = useState<
        GetCloudStatusType | undefined
    >(undefined);

    useEffect(() => {
        if (platform === "ccp") {
            setCloudStatus(() => async () => {
                if (!provisioningRequestId) {
                    return Promise.resolve(false);
                }
                const requestIdsResp = async (product: string) =>
                    await stargateClient.getProductActivationStatus({
                        provisioningRequestId: product
                    });

                const requestIds = await Promise.all(
                    provisioningRequestId.split(",").map(async (product) => {
                        try {
                            console.info(
                                `Requesting Ids ${JSON.stringify(product)}`
                            );
                            return await requestIdsResp(product);
                        } catch {
                            // retry if the first call fails
                            console.info(
                                `Retry Requesting Ids ${JSON.stringify(
                                    product
                                )}`
                            );
                            return await requestIdsResp(product);
                        }
                    })
                );

                // check for cloudId in the status response
                if (!cloudId) {
                    const detectedCloudId =
                        getCloudIdFromProductActivationStatuses(requestIds);
                    if (detectedCloudId) {
                        setCloudId(detectedCloudId);
                    }
                }

                for (const resource of requestIds) {
                    if (
                        resource.resourcePackages.find(
                            (item) => item.action.status === "IN_PROGRESS"
                        )
                    )
                        return Promise.resolve(false);

                    if (
                        resource.resourcePackages.find(
                            (item) =>
                                item.action.status === "PROVISIONING_FAILURE"
                        )
                    ) {
                        return Promise.reject(
                            new Error(
                                `Provisioning Failure CCP with cloudId ${cloudId}: ${JSON.stringify(
                                    resource.resourcePackages[0]?.action
                                        .errorDetails
                                )}`
                            )
                        );
                    }
                }

                return Promise.resolve(true);
            });
        } else {
            setCloudStatus(() => async () => {
                if (!cloudId || !requestId) {
                    return Promise.resolve(false);
                }

                const cloudStatusResp = async () => {
                    const { completed, successful } =
                        await cofsClient.getCloudStatus({
                            cloudId,
                            requestId
                        });
                    return Promise.resolve(completed && successful);
                };

                try {
                    return await cloudStatusResp();
                } catch {
                    // retry if the first call fails
                    return await cloudStatusResp();
                }
            });
        }
    }, [platform, requestId]);

    useEffect(() => {
        if (!hasRequiredParameters) {
            if (createCloudStatus !== "error") {
                createAnalyticsEvent({
                    event: "errored",
                    eventName: "missingRequiredParameters"
                }).fire("signup");
                setCreateCloudStatus("error");
            }
            return;
        }
        getCloudStatus &&
            pollCloudStatusWithFeatureFlag(
                {
                    getCloudStatus,
                    startTime: Date.now(),
                    retryInterval,
                    timeout,
                    onSuccess: (startTime, endTime, elapsedTime) => {
                        if (is15secondProvisioningEnabled) {
                            createAnalyticsEvent({
                                eventType: "operational",
                                action: "succeeded",
                                actionSubject:
                                    "provisioning-loading-screen-with-improved-polling",
                                source: "bxpkit-wac-confirmation-page",
                                attributes: {
                                    totalElapsedTime: endTime - startTime,
                                    timeDiffFromPrev5secondPoll:
                                        (endTime - startTime) % 5000,
                                    timeDiffFromNext5secondPoll:
                                        5000 - ((endTime - startTime) % 5000)
                                }
                            }).fire("signup");
                        }

                        createAnalyticsEvent({
                            eventType: "operational",
                            eventName: "createCloudSucceeded",
                            startTime,
                            endTime,
                            elapsedTime
                        }).fire("signup");
                        setProgress(1); // Progress bar to 100% before redirecting user to product
                        setCreateCloudStatus("success");
                        if (
                            !shouldShowUserSegmentationPage &&
                            featureFlags.provisionSecondPartyApps &&
                            provisionApp &&
                            platform !== "ccp" &&
                            !!appKey &&
                            !!onDemandUrl
                        ) {
                            // eslint-disable-next-line react-hooks/rules-of-hooks
                            const bxpExpressClient = useBXPExpressClient();
                            void bxpExpressClient
                                .getXsrfToken()
                                .then((xsrfToken) => {
                                    bxpExpressClient
                                        .marketplaceAppInstall({
                                            siteName: onDemandUrl,
                                            appKey,
                                            xsrfToken
                                        })
                                        .catch((err) => err);
                                });
                        }
                    },
                    onError: (startTime, endTime, elapsedTime, error) => {
                        createAnalyticsEvent({
                            event: "errored",
                            eventName: "createCloudErrored",
                            startTime,
                            endTime,
                            elapsedTime,
                            error
                        }).fire("signup");

                        setCreateCloudStatus("error");
                    },
                    onTimeout: (startTime, endTime, elapsedTime) => {
                        createAnalyticsEvent({
                            event: "timedOut",
                            eventName: "createCloudTimedOut",
                            startTime,
                            endTime,
                            elapsedTime
                        }).fire("signup");
                        setCreateCloudStatus("timeout");
                    }
                },
                is15secondProvisioningEnabled
            );
    }, [hasRequiredParameters, getCloudStatus]);

    // Handles the progress bar update
    // https://hello.atlassian.net/wiki/spaces/BXP/pages/2602964460/USC-898+Fixing+the+dilemma+of+the+progress+bar+by+implementing+a+piecewise+function
    useEffect(() => {
        const startTime = Date.now();
        const pivotPercentage = 0.8; // The progress bar will fill up to 80% by the pivot time
        const progressBarPivotPoint = is15secondProvisioningEnabled
            ? 30 * 1000
            : 60 * 1000; // the pivot point after which the progress bar will slow down
        const slowProgressIncrement =
            (1 - pivotPercentage) /
            ((DEFAULT_TIMEOUT - progressBarPivotPoint) / 100);
        const slowProgress = 0;

        const interval = setInterval(() => {
            setProgress(
                getProgressbarStatus({
                    startTime,
                    pivotPercentage,
                    progressBarPivotPoint,
                    slowProgressIncrement,
                    slowProgress,
                    interval
                })
            );
        }, 100); // Update progress every 0.1 second

        return () => {
            setProgress(0);
            clearInterval(interval);
        };
    }, [is15secondProvisioningEnabled]);

    useEffect(() => {
        if (createCloudStatus !== "success") {
            return;
        }

        if (
            // verify user has made it to a final screen (i.e. robots, jira-animated-provisioning-screen, redirect).
            finalProvisioningScreens.includes(currentScreen) &&
            (isFreeBundlesEnabled || isPageOverlayModalEnabled)
        ) {
            switch (bundleProvisioningStatus) {
                case "payload-ready":
                case "no-payload":
                    // initial site is provisioned, now let Bundles UI know
                    // it can provision secondary products
                    setBundleProvisioningStatus("provisioning");
                    return;
                case "success":
                    if (deferRedirect) {
                        setRedirectUrl(createRedirectUrl());
                        return;
                    }

                    createAnalyticsEvent({
                        eventName: "redirectedToProduct",
                        eventComponent: "confirmationPage",
                        event: "redirected",
                        eventType: "ui",
                        action: "redirected"
                    }).fire();

                    // Bundles UI has completed provisioning secondary products
                    window.location.assign(createRedirectUrl());
                    return;
                case "waiting":
                    // Bundles modal should alert the user that product is ready
                    if (isPageOverlayModalEnabled) {
                        setBundleProvisioningStatus("provisioning");
                    }
                    return;
                default:
                    return;
            }
        }

        // verify user has made it to a final screen (i.e. robots, jira-animated-provisioning-screen, redirect).
        if (finalProvisioningScreens.includes(currentScreen)) {
            if (deferRedirect) {
                setRedirectUrl(createRedirectUrl());
                return;
            }

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected"
            }).fire();

            window.location.assign(createRedirectUrl());
        }
    }, [currentScreen, createCloudStatus, bundleProvisioningStatus]);

    return (
        <CloudStatusContext.Provider
            value={{
                isReferredFromVerifyEmail,
                createCloudStatus,
                progress,
                featureFlags,
                deferRedirect,
                setDeferRedirect,
                redirectUrl
            }}
        >
            {children}
        </CloudStatusContext.Provider>
    );
};

export type PollCloudStatusOptions = {
    readonly getCloudStatus: GetCloudStatusType;
    readonly startTime: number;
    readonly retryInterval: number;
    readonly timeout: number;

    readonly onSuccess: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
    readonly onError: (
        startTime: number,
        endTime: number,
        elapsedTime: number,
        error: unknown
    ) => void;
    readonly onTimeout: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
    // readonly onResponse: (elapsedTime: number) => void;
};

export const getPrimaryProduct = (products: readonly string[]) => {
    if (products.length > 0 && products[0] !== "") {
        return products[0];
    } else {
        return undefined;
    }
};

const getTopLevelDomain = (isProduction: boolean): string => {
    if (isProduction) {
        return ".atlassian.net";
    } else {
        return ".jira-dev.com";
    }
};

const isValidOnDemandUrl = (siteName: string) => {
    const isUrl = siteName.includes(".");
    return !isUrl;
};

const createBaseUrl = (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    products: readonly string[],
    dataRegion: string | undefined,
    cloudId: string,
    admin: boolean | undefined,
    createAnalyticsEvent: CreateUIAnalyticsEvent
): string => {
    if (!isValidOnDemandUrl(onDemandUrl || "")) {
        createAnalyticsEvent({
            actionSubject: "invalidUrlOnRedirect",
            eventComponent: "confirmationPage",
            event: "event",
            eventType: "ui"
        }).fire("signup");
        window.history.back();
    }
    const instanceUrl =
        "https://" +
        encodeURIComponent(String(onDemandUrl)) +
        getTopLevelDomain(isProduction);

    const primaryProduct: string | undefined = getPrimaryProduct(products);

    if (admin) {
        return getAdminhubRedirectUrl(isProduction, cloudId);
    }

    if (typeof primaryProduct !== "string") {
        return instanceUrl;
    }

    if (primaryProduct === "opsgenie") {
        return getOpsgenieRedirectUrl(isProduction, onDemandUrl, dataRegion);
    }

    if (primaryProduct === "statuspage") {
        return getStatuspageRedirectUrl(isProduction, cloudId);
    }

    if (primaryProduct === "com.atlassian.bitbucket") {
        return getBitbucketRedirectUrl(isProduction);
    }

    if (primaryProduct === "confluence.ondemand") {
        return getConfluenceJiraRedirectUrl(instanceUrl);
    }
    return instanceUrl;
};

const getConfluenceJiraRedirectUrl = function (instanceUrl: string) {
    return instanceUrl + "/wiki";
};

const getBitbucketRedirectUrl = function (isProduction: boolean) {
    if (!isProduction) {
        return "https://integration.bb-inf.net/dashboard/overview";
    }

    return "https://bitbucket.org/dashboard/overview";
};

const getOpsgenieRedirectUrl = function (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    dataRegion: string | undefined
) {
    if (!isProduction) {
        return `https://${onDemandUrl}.atlassian-app.opsgeni.us`;
    }
    if (dataRegion === "europe") {
        return `https://${onDemandUrl}.atlassian-app.eu.opsgenie.com`;
    }
    return `https://${onDemandUrl}.atlassian-app.opsgenie.com`;
};

const getStatuspageRedirectUrl = function (
    isProduction: boolean,
    cloudId: string
) {
    if (!isProduction) {
        return "https://manage.statuspagestaging.com/cloud/" + cloudId;
    }

    return "https://manage.statuspage.io/cloud/" + cloudId;
};

const getAdminhubRedirectUrl = (isProduction: boolean, cloudId: string) => {
    if (!isProduction) {
        return "https://admin.stg.atlassian.com/s/" + cloudId + "/setup";
    }

    return "https://admin.atlassian.com/s/" + cloudId + "/setup";
};

const getCloudIdFromProductActivationStatuses = (
    ccpResponses: ProductActivationStatusResponse[]
): string | undefined =>
    ccpResponses
        .map(({ resourcePackages }) =>
            resourcePackages
                .map(
                    ({ resourceAris }) =>
                        resourceAris
                            .find((resourceAri) =>
                                resourceAri.startsWith(
                                    "ari:cloud:platform::site/"
                                )
                            )
                            ?.split("/")[1]
                )
                .find((e) => e)
        )
        .find((e) => e);

function pollCloudStatusWithFeatureFlag(
    options: PollCloudStatusOptions,
    is15secondProvisioningEnabled: boolean
): void {
    if (is15secondProvisioningEnabled) {
        return pollCloudStatusFor15secondProvisioning(options);
    }
    return pollCloudStatus(options);
}

function pollCloudStatus(options: PollCloudStatusOptions): void {
    const {
        getCloudStatus,
        startTime,
        retryInterval,
        timeout,
        onSuccess,
        onError,
        onTimeout
    } = options;
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;
    // onResponse(elapsedTime);
    if (elapsedTime < timeout) {
        getCloudStatus()
            .then((ready) => {
                if (ready) {
                    onSuccess(startTime, endTime, elapsedTime);
                } else {
                    setTimeout(() => pollCloudStatus(options), retryInterval);
                }
            })
            .catch((error) => onError(startTime, endTime, elapsedTime, error));
    } else {
        onTimeout(startTime, endTime, elapsedTime);
    }
}

/**
 * As a part of https://team.atlassian.com/project/ATLAS-37332, Jira site provisioning has been reduced to about 15
 * seconds. This polling function has been optimised to poll faster during the expected provisioning completion range to
 * land customers in the site faster.
 */
export function pollCloudStatusFor15secondProvisioning(
    options: PollCloudStatusOptions,
    run = 0
): void {
    const {
        getCloudStatus,
        startTime,
        timeout,
        onSuccess,
        onError,
        onTimeout
    } = options;

    function poll() {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;

        if (elapsedTime > timeout) {
            onTimeout(startTime, endTime, elapsedTime);
            return;
        }

        getCloudStatus()
            .then((ready) => {
                if (ready) {
                    onSuccess(startTime, endTime, elapsedTime);
                } else {
                    pollCloudStatusFor15secondProvisioning(options, run + 1);
                }
            })
            .catch((error) => onError(startTime, endTime, elapsedTime, error));
    }

    // initial run, check immediately to avoid rate limiting issues
    if (run === 0) {
        poll();
        return;
    }
    // we expect the majority of requests to be completed in the 15-30 second range, wait until 15 seconds to begin fast polling
    if (run === 1) {
        setTimeout(() => poll(), 15000);
        return;
    }
    // in the 15-30 seconds range, fast poll every 2 seconds
    if (run > 1 && run < 10) {
        setTimeout(() => poll(), 2000);
        return;
    }
    // if provisioning is delayed, slow poll every 10 seconds until the timeout if necessary
    setTimeout(() => poll(), 10000);
}

/**
 * useConfirmationPageOverlayModal
 *
 * Hook that determines whether or not to render the bundle or video modal overlay UI.
 * Checks for enable-confirmation-page-overlay feature flag,
 * ensures that enable-confirmation-page-overlay applies only to
 * single-product signups for Confluence.
 *
 * @returns boolean
 */
export function useConfirmationPageOverlayModal(
    featureFlags: FeatureFlags
): boolean {
    const { products, cloudId } = useSearchParameters();
    return (
        !!featureFlags?.enableConfirmationPageOverlay &&
        (featureFlags.enableConfirmationPageOverlay ===
            ConfirmationPageOverlayVariants.BUNDLES_MODAL ||
            featureFlags.enableConfirmationPageOverlay ===
                ConfirmationPageOverlayVariants.VIDEO_OVERLAY) &&
        products.length === 1 &&
        cloudId !== "" &&
        products.includes("confluence.ondemand")
    );
}
